<script setup>
import { computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useSidebarContext } from './provider';
import Icon from 'next/icon/Icon.vue';

const store = useStore();
const { t } = useI18n();
const { isCollapsed } = useSidebarContext();

const isConnected = useMapGetter('googleWorkspace/isConnected');
const googleEmail = useMapGetter('googleWorkspace/getGoogleEmail');

// Each button opens the agent's own Google Workspace app in a new tab. The
// `authuser` query pins it to the account matching their Chatwoot login even
// when several Google accounts are signed in on the browser.
const apps = computed(() => [
  {
    label: t('SIDEBAR.GOOGLE_WORKSPACE.DOCS'),
    icon: 'i-lucide-file-text',
    url: 'https://docs.google.com/document/u/0/',
  },
  {
    label: t('SIDEBAR.GOOGLE_WORKSPACE.SHEETS'),
    icon: 'i-lucide-sheet',
    url: 'https://docs.google.com/spreadsheets/u/0/',
  },
  {
    label: t('SIDEBAR.GOOGLE_WORKSPACE.SLIDES'),
    icon: 'i-lucide-presentation',
    url: 'https://docs.google.com/presentation/u/0/',
  },
  {
    label: t('SIDEBAR.GOOGLE_WORKSPACE.DRIVE'),
    icon: 'i-lucide-folder',
    url: 'https://drive.google.com/drive/u/0/',
  },
]);

const openApp = app => {
  if (!isConnected.value) {
    store.dispatch('googleWorkspace/connect');
    return;
  }

  const email = googleEmail.value;
  const target = email
    ? `${app.url}?authuser=${encodeURIComponent(email)}`
    : app.url;
  window.open(target, '_blank', 'noopener');
};

const disconnect = async () => {
  await store.dispatch('googleWorkspace/disconnect');
  useAlert(t('SIDEBAR.GOOGLE_WORKSPACE.DISCONNECTED'));
};

// The OAuth callback redirects back with ?google_workspace=<result>. Surface the
// outcome as a toast and strip the param so a refresh doesn't repeat it.
const handleOAuthReturn = () => {
  const params = new URLSearchParams(window.location.search);
  const result = params.get('google_workspace');
  if (!result) return;

  let message = '';
  if (result === 'connected') {
    message = t('SIDEBAR.GOOGLE_WORKSPACE.CONNECTED_SUCCESS');
    store.dispatch('googleWorkspace/get');
  } else if (result === 'email_mismatch') {
    message = t('SIDEBAR.GOOGLE_WORKSPACE.EMAIL_MISMATCH');
  } else if (result === 'error') {
    message = t('SIDEBAR.GOOGLE_WORKSPACE.CONNECT_ERROR');
  }
  if (message) useAlert(message);

  params.delete('google_workspace');
  const query = params.toString();
  window.history.replaceState(
    {},
    '',
    window.location.pathname + (query ? `?${query}` : '') + window.location.hash
  );
};

onMounted(() => {
  store.dispatch('googleWorkspace/get');
  handleOAuthReturn();
});
</script>

<template>
  <div
    class="flex flex-col gap-1 min-w-0"
    :class="isCollapsed ? 'items-center' : ''"
  >
    <button
      v-for="app in apps"
      :key="app.label"
      type="button"
      class="flex items-center gap-2 rounded-lg text-n-slate-11 hover:bg-n-blue-3/60 hover:text-n-slate-12 transition-colors"
      :class="isCollapsed ? 'justify-center size-10' : 'h-8 px-2 py-1 w-full'"
      :title="isConnected ? app.label : t('SIDEBAR.GOOGLE_WORKSPACE.CONNECT')"
      @click="openApp(app)"
    >
      <Icon :icon="app.icon" class="size-4" />
      <span v-if="!isCollapsed" class="flex-1 text-sm truncate text-start">
        {{ app.label }}
      </span>
    </button>
    <button
      v-if="isConnected && !isCollapsed"
      type="button"
      class="flex items-center gap-2 h-8 px-2 py-1 w-full rounded-lg text-n-slate-10 hover:bg-n-blue-3/60 hover:text-n-slate-12 transition-colors"
      :title="t('SIDEBAR.GOOGLE_WORKSPACE.DISCONNECT')"
      @click="disconnect"
    >
      <Icon icon="i-lucide-unplug" class="flex-shrink-0 size-4" />
      <span class="flex-1 text-sm truncate text-start">
        {{ t('SIDEBAR.GOOGLE_WORKSPACE.DISCONNECT') }}
      </span>
    </button>
  </div>
</template>
